/**
 * The debug console (backtick).
 *
 * Everything it does goes through the normal command queue, so anything you can
 * do here is something the simulation already understands — and a session with
 * console use is still a legal, replayable session.
 */

import { Cmd, cmd, type Command } from '../sim/commands.ts';
import type { World } from '../sim/world.ts';
import { UNIT_TYPES } from '../data/units.ts';
import { BUILDING_TYPES } from '../data/buildings.ts';
import { POWERS } from '../data/powers.ts';
import { RESOURCE_KEYS } from '../sim/types.ts';
import { hashWorldHex } from '../sim/hash.ts';
import { seedToCode } from '../core/rng.ts';
import { ageBlockedReason, waveBudget, AGE_NAMES } from '../sim/systems/director.ts';
import { CHRONICLE } from '../data/events.ts';
import { ageCost, ageResearchSeconds } from '../data/ages.ts';
import { heroAbilities, heroPointsLeft, heroXpProgress } from '../sim/systems/hero.ts';
import {
  dayOfNextMonth,
  daysToNextSeason,
  formatDateLong,
  formatDayLong,
  MONTH_NAMES,
  season,
} from '../sim/systems/calendar.ts';
import { HERO_CLASSES } from '../data/heroes.ts';
import { ITEMS, ITEM_SLOT_NAMES, itemDef } from '../data/items.ts';

const HELP = `
  spawn <unitId> [count] [enemy|ally]   put units at the Hearth
  give <resource> <amount>              timber planks stone iron grain bread souls
  setwave <n>                           jump the director to wave n
  lesson [1-5|0]                        open the guided run at a lesson; 0 leaves it
  killall                               remove every invader on the field
  god                                   heal the Hearth to full
  timescale <n>                         0 - 8; the keys walk 0.5 1 2 4 8, P pauses
  seed                                  print the run seed
  hash                                  print the deterministic state hash
  budget [wave]                         print the director's threat budget
  power <id>                            unlock a Sovereign Power
  build <id> [dx] [dy]                  place a building, offset from the Hearth
  age [go]                              the Age and its price; 'age go' commits
  setage <n>                            jump the Age gate (the harness's door)
  date [day|month]                      the ledger; a number or a month jumps it
  event [id]                            draw a Chronicle event (blank = next eligible)
  hero [classId]                        print the character sheet, or pick one
  xp <amount>                           give the hero experience
  skill <skillId>                       spend a point on the hero's tree
  item [id] [level]                     read the cart, or put something on it
  ring [radius] [gates] [tier]          lay a closed wall circuit round the Hearth
  towers [n] [radius]                   raise n towers evenly around the ring
  breach [radius]                       knock a hole and a gap in the ring
  man [radius]                          spread every company round the wall
  town [tier] [fort] [dx] [dy]          raise an AI town, offset from the Hearth
  list units|buildings|powers|heroes    dump ids
  clear                                 clear this output
`;

/** The default ring radius. Wide enough to hold a town, close enough to man. */
const RING_R = 22;

export class DebugConsole {
  private el: HTMLElement;
  private out: HTMLElement;
  private input: HTMLInputElement;
  private history: string[] = [];
  private historyIdx = 0;
  open = false;

  constructor(
    private send: (c: Command) => void,
    private getWorld: () => World,
  ) {
    this.el = document.createElement('div');
    this.el.id = 'console';
    this.out = document.createElement('div');
    this.out.id = 'console-out';
    this.input = document.createElement('input');
    this.input.id = 'console-in';
    this.input.spellcheck = false;
    this.input.autocomplete = 'off';
    this.el.appendChild(this.out);
    this.el.appendChild(this.input);
    (document.getElementById('ui-root') as HTMLElement).appendChild(this.el);

    this.print('The Last Hearth — console. Type `help`.');

    this.input.addEventListener('keydown', (e) => {
      // The window-level handler never sees these: the input stops propagation
      // so that WASD does not pan the camera while you are typing. Which means
      // closing the console has to be handled right here.
      if (e.key === '`' || e.key === 'Escape') {
        this.toggle();
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key === 'Enter') {
        const line = this.input.value.trim();
        this.input.value = '';
        if (line) {
          this.history.push(line);
          this.historyIdx = this.history.length;
          this.print(`> ${line}`);
          this.run(line);
        }
        e.stopPropagation();
      } else if (e.key === 'ArrowUp') {
        if (this.historyIdx > 0) this.input.value = this.history[--this.historyIdx] ?? '';
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        if (this.historyIdx < this.history.length - 1) this.input.value = this.history[++this.historyIdx] ?? '';
        else this.input.value = '';
        e.preventDefault();
      }
      e.stopPropagation();
    });
  }

  toggle(): void {
    this.open = !this.open;
    this.el.classList.toggle('show', this.open);
    if (this.open) setTimeout(() => this.input.focus(), 0);
    else this.input.blur();
  }

  /**
   * Top the stores up, immediately before something that spends.
   *
   * A `give` on its own does not survive to the next console line: the stores
   * are clamped to `resCap` at the end of every tick, and the harness leaves
   * ninety milliseconds between lines. Everything pushed from *one* line lands
   * in one `applyCommands`, before anything clamps — so a builder that funds
   * itself in the same breath actually gets to build. This is why `ring` lays a
   * whole circuit and `give timber 6000; ring` laid a third of one.
   */
  private fund(): void {
    for (const k of ['timber', 'stone', 'iron', 'planks'] as const) {
      this.send(cmd(Cmd.DebugGive, 4000, 0, 0, 0, k));
    }
  }

  private print(s: string): void {
    this.out.textContent += (this.out.textContent ? '\n' : '') + s;
    this.out.scrollTop = this.out.scrollHeight;
  }

  private run(line: string): void {
    const parts = line.split(/\s+/);
    const c = parts[0].toLowerCase();
    const w = this.getWorld();

    switch (c) {
      case 'help':
        this.print(HELP.trim());
        break;
      case 'clear':
        this.out.textContent = '';
        break;
      case 'spawn': {
        const id = parts[1];
        const count = Number(parts[2] ?? 1) || 1;
        const enemy = (parts[3] ?? 'enemy').startsWith('e') ? 1 : 0;
        // An optional distance puts them that many tiles *north* of the Hearth,
        // which is how you get a horde that is actually outside your own walls.
        // Zero keeps the old behaviour, so every existing script is unchanged.
        const dist = Math.round(Number(parts[4] ?? 0) || 0);
        if (!UNIT_TYPES.some((t) => t.id === id)) {
          this.print(`no such unit: ${id}`);
          break;
        }
        const sx = dist === 0 ? w.map.hearthX + 4 : w.map.hearthX - 1;
        const sy = w.map.hearthY - dist;
        this.send(cmd(Cmd.DebugSpawn, sx, sy, count, enemy, id));
        this.print(`spawned ${count} × ${id}${dist ? ` ${dist} north` : ''}`);
        break;
      }
      case 'give': {
        const k = parts[1];
        const n = Number(parts[2] ?? 100) || 100;
        if (!RESOURCE_KEYS.includes(k as never)) {
          this.print(`resources: ${RESOURCE_KEYS.join(' ')}`);
          break;
        }
        this.send(cmd(Cmd.DebugGive, n, 0, 0, 0, k));
        this.print(`+${n} ${k}`);
        break;
      }
      case 'setwave': {
        const n = Number(parts[1] ?? 1) || 1;
        this.send(cmd(Cmd.DebugSetWave, n));
        this.print(`director set to wave ${n}`);
        break;
      }
      /**
       * The guided run, from a chosen lesson. `lesson` alone opens at the
       * first; `lesson 0` leaves them. The same two commands the menu button
       * and the Skip link send, which is what makes a console-driven
       * screenshot a picture of the real thing.
       */
      case 'lesson': {
        const n = Math.max(0, Math.min(5, Math.floor(Number(parts[1] ?? 1)) || 0));
        if (n === 0) {
          this.send(cmd(Cmd.TutorialSkip));
          this.print('the lessons are behind you');
        } else {
          this.send(cmd(Cmd.TutorialBegin, n));
          this.print(`lesson ${n}: the next wave is bent to teach it`);
        }
        break;
      }
      case 'killall':
        this.send(cmd(Cmd.DebugKillAll));
        this.print('the field is clear');
        break;
      case 'town': {
        // Offset from the Hearth like `build`, because the one thing you want
        // to do with a generated town is stand next to it and look at it.
        const tier = Math.max(1, Math.min(10, Number(parts[1] ?? 5) || 5));
        const fort = Math.max(0, Math.min(3, Number(parts[2] ?? 2)));
        const dx = Number(parts[3] ?? 60) || 0;
        const dy = Number(parts[4] ?? 0) || 0;
        this.send(cmd(Cmd.RaiseTown, w.map.hearthX + dx, w.map.hearthY + dy, tier, fort));
        this.print(`a town of the ${tier}th rung goes up, fort ${fort}`);
        break;
      }
      case 'god':
        this.send(cmd(Cmd.DebugGod));
        this.print('the Hearth burns bright');
        break;
      case 'timescale': {
        const n = Number(parts[1] ?? 1);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (globalThis as any).hearth?.setTimescale(n);
        this.print(`timescale ${n}`);
        break;
      }
      case 'seed':
        this.print(`${w.config.seed}  (${seedToCode(w.config.seed)})`);
        break;
      case 'hash':
        this.print(`tick ${w.tick}  hash ${hashWorldHex(w)}`);
        break;
      case 'budget': {
        const n = Number(parts[1] ?? w.wave + 1) || w.wave + 1;
        this.print(`wave ${n} threat budget: ${waveBudget(w, n).toFixed(0)}`);
        break;
      }
      case 'age': {
        if (w.researchingAge > 0) {
          const need = ageResearchSeconds(w.researchingAge);
          this.print(
            `researching ${AGE_NAMES[w.researchingAge - 1] ?? 'Hollow'} — ` +
              `${w.ageResearch.toFixed(0)}/${need}s. It cannot be stopped.`,
          );
          break;
        }
        if (parts[1] !== 'go') {
          const next = w.age + 1;
          const why = ageBlockedReason(w);
          const cost = Object.entries(ageCost(next))
            .map(([k, v]) => `${v} ${k}`)
            .join(', ');
          this.print(
            `${AGE_NAMES[w.age - 1] ?? 'Hollow'} — Age ${w.age}.\n` +
              (why
                ? `next: blocked — ${why}`
                : `next: ${AGE_NAMES[next - 1] ?? 'Hollow'} for ${cost}, ` +
                  `${ageResearchSeconds(next)}s of work. \`age go\` to commit.`),
          );
          break;
        }
        this.send(cmd(Cmd.AdvanceAge));
        this.print('committing to the next Age.');
        break;
      }
      /**
       * `date` prints the ledger; `date <month>` or `date <n>` jumps it.
       *
       * The month-name form resolves to an absolute day *here*, in the UI, so
       * the command that reaches the simulation carries a plain number and the
       * replay does not depend on how a month was spelled.
       */
      case 'date': {
        const arg = parts[1];
        if (!arg) {
          const s = season(w);
          this.print(
            `${formatDateLong(w)}\n` +
              `day ${w.calendarDay} · ${daysToNextSeason(w)} days until the season turns\n` +
              `${s.rules}\n` +
              `months: ${MONTH_NAMES.join(' ')}`,
          );
          break;
        }
        const n = Number(arg);
        const day = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : dayOfNextMonth(w, arg);
        if (day < 0) {
          this.print(`months: ${MONTH_NAMES.join(' ')}`);
          break;
        }
        this.send(cmd(Cmd.DebugSetDate, day));
        this.print(`ledger set to day ${day} — ${formatDayLong(day)}`);
        break;
      }
      case 'power': {
        const id = parts[1];
        if (!POWERS.some((p) => p.id === id)) {
          this.print(`powers: ${POWERS.map((p) => p.id).join(' ')}`);
          break;
        }
        if (!w.unlockedPowers.includes(id)) w.unlockedPowers.push(id);
        this.print(`unlocked ${id}`);
        break;
      }
      /**
       * `build <id> [dx] [dy]` — placed relative to the Hearth.
       *
       * The offsets are new and they exist for the silhouette pass: a screenshot
       * that answers "can you name this building from its outline" has to be able
       * to lay a row of them out side by side, and a command that always drops on
       * the same two tiles can place exactly one thing.
       */
      case 'build': {
        const id = parts[1];
        const idx = BUILDING_TYPES.findIndex((b) => b.id === id);
        if (idx < 0) {
          this.print(`no such building: ${id}`);
          break;
        }
        const dx = Number.isFinite(Number(parts[2])) && parts[2] !== undefined ? Number(parts[2]) : 5;
        const dy = Number.isFinite(Number(parts[3])) && parts[3] !== undefined ? Number(parts[3]) : 5;
        this.send(cmd(Cmd.Build, idx, w.map.hearthX + dx, w.map.hearthY + dy));
        this.print(`placed ${id} at ${dx > 0 ? '+' : ''}${dx}, ${dy > 0 ? '+' : ''}${dy}`);
        break;
      }
      /**
       * `setage <n>` — the harness's door into the Age gate.
       *
       * `age go` is the game's, and it costs the Age's price and takes its
       * minutes. This one is for the shot harness, which needs a settlement that
       * may legally place a Wardstone Tower without playing fifteen waves first.
       */
      case 'setage': {
        const n = Number(parts[1]);
        if (!Number.isFinite(n)) {
          this.print(`age is ${w.age}. \`setage <n>\` to jump, \`age go\` to buy it properly.`);
          break;
        }
        this.send(cmd(Cmd.DebugSetAge, Math.round(n)));
        this.print(`age → ${Math.round(n)}`);
        break;
      }
      case 'event': {
        const id = parts[1] ?? '';
        if (id && !CHRONICLE.some((e) => e.id === id)) {
          this.print(`chronicle: ${CHRONICLE.map((e) => e.id).join(' ')}`);
          break;
        }
        this.send(cmd(Cmd.DebugEvent, 0, 0, 0, 0, id));
        this.print(id ? `drawing ${id}` : 'drawing the next eligible event');
        break;
      }
      case 'hero': {
        const id = parts[1];
        if (id) {
          if (!HERO_CLASSES.some((h) => h.id === id)) {
            this.print(`heroes: ${HERO_CLASSES.map((h) => h.id).join(' ')}`);
            break;
          }
          this.send(cmd(Cmd.PickHero, 0, 0, 0, 0, id));
          this.print(`picked ${id} (only takes before the first horn)`);
          break;
        }
        if (w.hero.classId === '') {
          this.print('no hero this run.');
          break;
        }
        const p = heroXpProgress(w);
        this.print(
          `${w.hero.name} — ${w.hero.classId}, level ${w.hero.level}\n` +
            `xp ${Math.floor(p.into)}/${Math.floor(p.need)}  points ${heroPointsLeft(w)}` +
            `${w.hero.fallen ? `  DOWN (${Math.ceil(w.hero.reviveT)}s)` : ''}\n` +
            `skills: ${w.hero.skills.join(' ') || '(none)'}\n` +
            `abilities: ${heroAbilities(w).join(' ') || '(none)'}`,
        );
        break;
      }
      case 'xp': {
        const n = Number(parts[1] ?? 100) || 100;
        this.send(cmd(Cmd.DebugGive, n, 0, 0, 0, 'heroxp'));
        this.print(`+${n} hero xp`);
        break;
      }
      case 'skill': {
        const id = parts[1];
        if (!id) {
          this.print('skill <skillId> — see `hero` for the class, and the sheet for the tree');
          break;
        }
        this.send(cmd(Cmd.HeroSkill, 0, 0, 0, 0, id));
        this.print(`spending a point on ${id}`);
        break;
      }
      /**
       * `item <id> [level]` — put a thing on the cart, `item` alone to read it.
       *
       * Goes through `Cmd.DebugGrantItem` like everything else in here, so a
       * session that used it is still a legal replay.
       */
      case 'item': {
        const id = parts[1];
        if (!id) {
          const worn = w.equipped
            .map((uid, s) =>
              uid < 0
                ? null
                : `${ITEM_SLOT_NAMES[s]}: ${itemDef(w.inventory.find((k) => k.uid === uid)?.defId ?? '')?.name ?? '?'}`,
            )
            .filter((line): line is string => line !== null);
          this.print(
            `cart ${w.inventory.length}\n` +
              (worn.join('\n') || '(nothing worn)') +
              `\n` +
              w.inventory
                .map((s) => `#${s.uid} ${s.defId} L${s.level}${w.equipped.includes(s.uid) ? ' (worn)' : ''}`)
                .join('\n'),
          );
          break;
        }
        if (!ITEMS.some((d) => d.id === id)) {
          this.print(`items: ${ITEMS.map((d) => d.id).join(' ')}`);
          break;
        }
        const lvl = Math.max(1, Number(parts[2] ?? 1) || 1);
        this.send(cmd(Cmd.DebugGrantItem, lvl, 0, 0, 0, id));
        this.print(`granting ${id} at level ${lvl}`);
        break;
      }
      /**
       * `ring [radius] [gates]` — a closed square circuit round the Hearth, with
       * a gate in the middle of each face.
       *
       * Four `WallLine` drags and four one-tile `WallLine`s, which is exactly
       * what a player would do with the mouse; nothing here can build anything a
       * player could not. It exists because *looking at a siege* is how this
       * feature is judged, and setting one up by hand from a screenshot harness
       * is forty drags.
       */
      case 'ring': {
        const r = Math.max(4, Math.floor(Number(parts[1] ?? RING_R) || RING_R));
        const gates = parts[2] === 'none' ? 0 : 1;
        const hx = w.map.hearthX;
        const hy = w.map.hearthY;
        const x0 = hx - r;
        const x1 = hx + r;
        const y0 = hy - r;
        const y1 = hy + r;
        this.fund();
        this.send(cmd(Cmd.WallLine, x0, y0, x1, y0));
        this.fund();
        this.send(cmd(Cmd.WallLine, x1, y0, x1, y1));
        this.fund();
        this.send(cmd(Cmd.WallLine, x1, y1, x0, y1));
        this.fund();
        this.send(cmd(Cmd.WallLine, x0, y1, x0, y0));
        this.fund();
        // Raise the whole circuit to the asked-for tier, one pass per step, so
        // `ring 16 gates 3` gives a bastion ring rather than a palisade.
        const tier = Math.max(1, Math.min(3, Math.floor(Number(parts[3] ?? 1) || 1)));
        for (let step = 1; step < tier; step++) {
          this.fund();
          this.send(cmd(Cmd.UpgradeWallLine, x0, y0, x1, y0));
          this.fund();
          this.send(cmd(Cmd.UpgradeWallLine, x1, y0, x1, y1));
          this.fund();
          this.send(cmd(Cmd.UpgradeWallLine, x1, y1, x0, y1));
          this.fund();
          this.send(cmd(Cmd.UpgradeWallLine, x0, y1, x0, y0));
        }
        this.fund();
        if (gates) {
          this.send(cmd(Cmd.WallLine, hx, y0, hx, y0, 'gate'));
          this.send(cmd(Cmd.WallLine, hx, y1, hx, y1, 'gate'));
          this.send(cmd(Cmd.WallLine, x0, hy, x0, hy, 'gate'));
          this.send(cmd(Cmd.WallLine, x1, hy, x1, hy, 'gate'));
        }
        this.print(`a ring at ${r} tiles${gates ? ', with four gates' : ''}`);
        break;
      }
      /**
       * `breach [radius]` — put the two faults a wall can have into the ring, so
       * the feedback that names them can be looked at.
       *
       * One stone out of the eastern face, which is a *hole* — curtain on both
       * sides of it — and four out of the northern, which is a *gap*, with a
       * dangling end at either side of it. They are drawn differently on purpose
       * and this is how you see both at once.
       */
      case 'breach': {
        const r = Math.max(4, Math.floor(Number(parts[1] ?? RING_R) || RING_R));
        const hx = w.map.hearthX;
        const hy = w.map.hearthY;
        this.send(cmd(Cmd.Demolish, hx + r, hy + 3));
        for (let n = 0; n < 4; n++) this.send(cmd(Cmd.Demolish, hx + 5 + n, hy - r));
        this.print('a hole in the east face, a gap in the north');
        break;
      }
      case 'towers': {
        const n = Math.max(1, Math.floor(Number(parts[1] ?? 8) || 8));
        const r = Math.max(4, Math.floor(Number(parts[2] ?? RING_R) || RING_R));
        const hx = w.map.hearthX;
        const hy = w.map.hearthY;
        // Walked round the square rather than placed by angle, so every tower
        // lands on the line rather than near it.
        const per = Math.max(1, Math.floor(n / 4));
        for (let side = 0; side < 4; side++) {
          for (let s = 0; s < per; s++) {
            const t = (s + 1) / (per + 1);
            const off = Math.round((t * 2 - 1) * (r - 2));
            const x = side === 0 ? hx + off : side === 1 ? hx + r : side === 2 ? hx + off : hx - r;
            const y = side === 0 ? hy - r : side === 1 ? hy + off : side === 2 ? hy + r : hy + off;
            this.fund();
            this.send(cmd(Cmd.WallTower, x - 1, y - 1));
          }
        }
        this.print(`raising ${per * 4} towers on the ring at ${r}`);
        break;
      }
      /**
       * `man [radius]` — every company takes the nearest stretch of wall.
       *
       * Each company is sent to the point of the circuit nearest to where it is
       * already standing, so a handful of companies spread themselves round the
       * ring instead of all piling onto the same face.
       */
      case 'man': {
        const r = Math.max(4, Math.floor(Number(parts[1] ?? RING_R) || RING_R));
        // `man 16 north` puts the whole garrison on one face, which is what you
        // want when you know where they are coming from — and what a screenshot
        // of a wall being held wants too.
        const face = (parts[2] ?? '').toLowerCase();
        const hx = w.map.hearthX;
        const hy = w.map.hearthY;
        let n = 0;
        const live = w.companies.filter((c) => c.team === 0 && !c.dead && c.members.length > 0);
        for (let i = 0; i < live.length; i++) {
          let x: number;
          let y: number;
          if (face) {
            // Spread along one face, centred, a company's frontage apart.
            const off = Math.round(((i - (live.length - 1) / 2) * (r * 2 - 4)) / Math.max(1, live.length));
            x = face === 'north' || face === 'south' ? hx + off : face === 'east' ? hx + r : hx - r;
            y = face === 'north' ? hy - r : face === 'south' ? hy + r : hy + off;
          } else {
            // Walk the perimeter and hand each company an equal share of it.
            const t = live.length === 1 ? 0.125 : i / live.length;
            const p = t * 4;
            const side = Math.floor(p) % 4;
            const f = (p - Math.floor(p)) * 2 - 1;
            const off = Math.round(f * (r - 1));
            x = side === 0 ? hx + off : side === 1 ? hx + r : side === 2 ? hx - off : hx - r;
            y = side === 0 ? hy - r : side === 1 ? hy + off : side === 2 ? hy + r : hy - off;
          }
          this.send(cmd(Cmd.Garrison, live[i].id, x, y));
          n++;
        }
        this.print(n > 0 ? `${n} companies to the wall${face ? ` (${face})` : ''}` : 'nobody to send');
        break;
      }
      case 'list': {
        const what = parts[1] ?? 'units';
        if (what.startsWith('u')) this.print(UNIT_TYPES.map((t) => t.id).join('  '));
        else if (what.startsWith('b')) this.print(BUILDING_TYPES.map((t) => t.id).join('  '));
        else if (what.startsWith('h')) {
          this.print(
            HERO_CLASSES.map((h) => `${h.id}: ${h.skills.map((s) => s.id).join(' ')}`).join('\n'),
          );
        } else this.print(POWERS.map((p) => p.id).join('  '));
        break;
      }
      default:
        this.print(`unknown: ${c}. try \`help\``);
        break;
    }
  }
}
